import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, CreditCard, Shield, LogOut, Save, Award, AlertTriangle, Phone, Bell, ExternalLink, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Navbar } from '@/components/Navbar';
import SavedCards from '@/components/SavedCards';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(AuthContext);
  const [bankAccount, setBankAccount] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBank, setSavingBank] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_name: '',
    bsb: '',
    account_number: '',
    payid: '',
    payid_type: 'email'
  });

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Verify token exists before fetching data
    const token = localStorage.getItem('session_token');
    if (!token) {
      console.log('No session token found, redirecting to auth');
      navigate('/auth');
      return;
    }
    const fetchData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        const config = { withCredentials: true, headers };
        
        const requests = [
          axios.get(`${API}/bank-account`, config).catch(() => ({ data: null })),
          axios.get(`${API}/users/phone`, config).catch(() => ({ data: { phone_number: '' } }))
        ];
        
        // Only fetch Stripe status for hosts
        if (user?.role === 'host') {
          requests.push(
            axios.get(`${API}/stripe/connect/status`, config).catch(() => ({ data: null }))
          );
        }
        
        const [bankRes, phoneRes, stripeRes] = await Promise.all(requests);
        
        if (bankRes.data) {
          setBankAccount(bankRes.data);
          setBankForm({
            account_name: bankRes.data.account_name || '',
            bsb: '',
            account_number: ''
          });
        }
        
        setPhoneNumber(phoneRes.data?.phone_number || '');
        
        if (stripeRes?.data) {
          setStripeStatus(stripeRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // If auth error, redirect to login
        if (error.response?.status === 401) {
          localStorage.removeItem('session_token');
          navigate('/auth');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.role]);

  // Scroll to bank section if user navigated here via Wallet → Connect bank
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const wantBank = params.get('focus') === 'bank' || location.hash === '#bank-section';
    if (!wantBank) return;
    // wait one tick so the element is mounted
    const t = setTimeout(() => {
      const el = document.getElementById('bank-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
    return () => clearTimeout(t);
  }, [loading, location.search, location.hash]);

  const handleConnectStripe = async () => {
    // Check if user is authenticated before making the call
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Please log in again to connect Stripe');
      navigate('/auth');
      return;
    }
    
    setConnectingStripe(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Use HTTPS URL for Stripe (required for live mode)
      // Get base URL from API endpoint or use current origin if already HTTPS
      const baseUrl = window.location.protocol === 'https:' 
        ? window.location.origin 
        : API.replace('/api', '');
      
      const res = await axios.post(`${API}/stripe/connect/onboard`, {
        return_url: `${baseUrl}/profile?stripe=success`,
        refresh_url: `${baseUrl}/profile?stripe=refresh`
      }, { withCredentials: true, headers });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      } else if (res.data.status === 'active') {
        toast.success('Your Stripe account is already active!');
        setStripeStatus({ status: 'active', charges_enabled: true, payouts_enabled: true });
      }
    } catch (error) {
      console.error('Stripe connect error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('session_token');
        navigate('/auth');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
      }
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/stripe/connect/dashboard`, { withCredentials: true, headers });
      if (res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.startsWith('+') || phoneNumber.length < 10) {
      toast.error('Please enter phone in E.164 format (e.g., +61412345678)');
      return;
    }

    setSavingPhone(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/users/phone`, { phone_number: phoneNumber }, { withCredentials: true, headers });
      toast.success('Phone number saved! You\'ll receive SMS notifications.');
    } catch (error) {
      toast.error('Failed to save phone number');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    
    if (!bankForm.account_name || !bankForm.bsb || !bankForm.account_number) {
      toast.error('Please fill in all bank details');
      return;
    }

    if (!/^\d{6}$/.test(bankForm.bsb.replace('-', ''))) {
      toast.error('BSB must be 6 digits');
      return;
    }

    // Validate PayID if provided
    if (bankForm.payid) {
      if (bankForm.payid_type === 'email' && !bankForm.payid.includes('@')) {
        toast.error('Please enter a valid email for PayID');
        return;
      }
      if (bankForm.payid_type === 'phone' && !/^[\d\s\-+]+$/.test(bankForm.payid)) {
        toast.error('Please enter a valid phone number for PayID');
        return;
      }
    }

    setSavingBank(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/bank-account`, {
        account_name: bankForm.account_name,
        bsb: bankForm.bsb.replace('-', ''),
        account_number: bankForm.account_number,
        payid: bankForm.payid || null,
        payid_type: bankForm.payid ? bankForm.payid_type : null
      }, { withCredentials: true, headers });
      
      setBankAccount({
        account_name: bankForm.account_name,
        account_masked: res.data.account_masked,
        bsb_masked: `***-${bankForm.bsb.slice(-3)}`,
        has_payid: res.data.has_payid,
        transfer_speed: res.data.transfer_speed
      });
      setBankForm({ ...bankForm, bsb: '', account_number: '', payid: '' });
      toast.success(res.data.has_payid ? 'Bank account saved with PayID for instant transfers!' : 'Bank account saved securely');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank account');
    } finally {
      setSavingBank(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      await axios.post(`${API}/auth/logout`, {}, { 
        withCredentials: true,
        headers 
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Always clear local state
    localStorage.removeItem('session_token');
    setUser(null);
    
    // Full page refresh to clear cached state
    window.location.href = '/';
  };

  const handleBecomeHost = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await axios.put(`${API}/auth/role`, { role: 'host' }, { 
        withCredentials: true,
        headers 
      });
      toast.success(res.data.is_early_adopter ? 'Congratulations! You\'re an Early Host!' : 'You\'re now a host!');
      setUser({ ...user, role: 'host', is_early_adopter: res.data.is_early_adopter });
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <div className="pt-20 flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <div className="pt-24 pb-12">
        <div className="container-main max-w-3xl">
          {/* Header */}
          <div className="flex items-center gap-6 mb-8">
            <Avatar className="h-20 w-20 border-4 border-[#5ebc67]">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#5ebc67] text-white text-2xl">
                {user?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                {user?.name}
              </h1>
              <p className="text-slate-600">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="capitalize">{user?.role}</Badge>
                {user?.is_early_adopter && (
                  <Badge className="badge-early-adopter">
                    <Award className="w-3 h-3 mr-1" />
                    Early Host
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Role Section */}
          {user?.role === 'driver' && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-[#5ebc67]" />
                  Account Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-900">Become a Host</p>
                    <p className="text-sm text-slate-500">List your driveway and start earning</p>
                  </div>
                  <Button onClick={handleBecomeHost} className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full">
                    Become Host
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Saved Payment Methods (for all users) */}
          <SavedCards />

          {/* Stripe Connect Section (Host only) */}
          {user?.role === 'host' && (
            <Card id="bank-section" className="mb-6 scroll-mt-24" data-testid="bank-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#5ebc67]" />
                  Stripe Payouts
                </CardTitle>
                <CardDescription>
                  Connect your Stripe account to receive automatic payouts when bookings are completed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!stripeStatus || stripeStatus.status === 'not_connected' ? (
                  <div className="space-y-4">
                    <Alert className="border-blue-200 bg-blue-50">
                      <CreditCard className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        Connect your Stripe account to receive payouts directly to your bank account.
                      </AlertDescription>
                    </Alert>
                    <Button 
                      onClick={handleConnectStripe} 
                      disabled={connectingStripe}
                      className="bg-[#635BFF] hover:bg-[#5851DB] text-white rounded-full"
                      data-testid="connect-stripe-btn"
                    >
                      {connectingStripe ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                      ) : (
                        <><CreditCard className="w-4 h-4 mr-2" /> Connect with Stripe</>
                      )}
                    </Button>
                  </div>
                ) : stripeStatus.status === 'active' ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Stripe Connected</p>
                        <p className="text-sm text-green-600">Your account is ready to receive payouts</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleOpenStripeDashboard}
                      variant="outline"
                      className="rounded-full"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
                      <div>
                        <p className="font-medium text-amber-800">Verification Pending</p>
                        <p className="text-sm text-amber-600">Stripe is reviewing your account</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleConnectStripe}
                      variant="outline"
                      className="rounded-full"
                    >
                      Complete Setup
                    </Button>
                  </div>
                )}
                
                <Separator className="my-6" />
                
                <div className="text-sm text-slate-500 space-y-2">
                  <p className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#5ebc67]" />
                    <strong>Automatic Payouts:</strong> Get paid within 2-7 business days
                  </p>
                  <p className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#5ebc67]" />
                    <strong>5% Fee:</strong> Platform fee deducted from each booking
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bank Account Section (Host only) - Legacy/Backup */}
          {user?.role === 'host' && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-slate-400" />
                  Bank Account & PayID
                </CardTitle>
                <CardDescription>
                  Receive payouts from completed bookings. Add PayID for instant transfers!
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!bankAccount ? (
                  <Alert className="mb-4 border-slate-200 bg-slate-50">
                    <AlertTriangle className="h-4 w-4 text-slate-600" />
                    <AlertDescription className="text-amber-800">
                      Add your bank account to receive payouts from completed bookings.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">Bank account connected</span>
                    </div>
                    <p className="text-slate-700">
                      <strong>{bankAccount.account_name}</strong>
                    </p>
                    <p className="text-sm text-slate-500">
                      BSB: {bankAccount.bsb_masked} | Account: {bankAccount.account_masked}
                    </p>
                    {bankAccount.has_payid && (
                      <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                        <span className="text-lg">⚡</span> PayID enabled - Instant transfers!
                      </p>
                    )}
                    {!bankAccount.has_payid && (
                      <p className="text-xs text-slate-400 mt-2">
                        Transfer speed: 1-2 business days (add PayID for instant transfers)
                      </p>
                    )}
                  </div>
                )}

                <form onSubmit={handleSaveBankAccount} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Account Name</Label>
                    <Input
                      placeholder="e.g., John Smith"
                      value={bankForm.account_name}
                      onChange={(e) => setBankForm(f => ({ ...f, account_name: e.target.value }))}
                      data-testid="bank-account-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>BSB</Label>
                      <Input
                        placeholder="XXX-XXX"
                        value={bankForm.bsb}
                        onChange={(e) => setBankForm(f => ({ ...f, bsb: e.target.value }))}
                        maxLength={7}
                        data-testid="bank-bsb"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={bankForm.account_number}
                        onChange={(e) => setBankForm(f => ({ ...f, account_number: e.target.value }))}
                        data-testid="bank-account-number"
                      />
                    </div>
                  </div>

                  {/* PayID Section */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">⚡</span>
                      <span className="font-medium text-amber-800">PayID for Instant Transfers</span>
                    </div>
                    <p className="text-xs text-amber-700">
                      Optional - Get paid instantly instead of waiting 1-2 business days
                    </p>
                    
                    <div className="flex gap-2 mb-3">
                      <Button
                        type="button"
                        variant={bankForm.payid_type === 'email' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setBankForm(f => ({ ...f, payid_type: 'email', payid: '' }))}
                        className={bankForm.payid_type === 'email' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      >
                        📧 Email
                      </Button>
                      <Button
                        type="button"
                        variant={bankForm.payid_type === 'phone' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setBankForm(f => ({ ...f, payid_type: 'phone', payid: '' }))}
                        className={bankForm.payid_type === 'phone' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      >
                        📱 Phone
                      </Button>
                    </div>
                    
                    <Input
                      placeholder={bankForm.payid_type === 'email' ? 'your@email.com' : '+61 400 000 000'}
                      value={bankForm.payid}
                      onChange={(e) => setBankForm(f => ({ ...f, payid: e.target.value }))}
                      data-testid="bank-payid"
                    />
                  </div>

                  <Button type="submit" disabled={savingBank} className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full">
                    <Save className="w-4 h-4 mr-2" />
                    {savingBank ? 'Saving...' : bankAccount ? 'Update Bank Account' : 'Save Bank Account'}
                  </Button>
                </form>

                <Separator className="my-6" />

                <div className="text-sm text-slate-500 space-y-2">
                  <p className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#5ebc67]" />
                    <strong>Security:</strong> Bank details are encrypted at rest
                  </p>
                  <p className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#5ebc67]" />
                    <strong>PayID:</strong> Instant transfers within minutes
                  </p>
                  <p className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#5ebc67]" />
                    <strong>Standard:</strong> 1-2 business days without PayID
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SMS Notifications */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#5ebc67]" />
                SMS Notifications
              </CardTitle>
              <CardDescription>
                Get instant SMS alerts for bookings and important updates.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSavePhone} className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number
                  </Label>
                  <Input
                    placeholder="+61412345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    data-testid="phone-input"
                  />
                  <p className="text-xs text-slate-500">Enter in E.164 format (e.g., +61412345678 for Australia)</p>
                </div>
                <Button type="submit" disabled={savingPhone} className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full">
                  <Save className="w-4 h-4 mr-2" />
                  {savingPhone ? 'Saving...' : 'Save Phone Number'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Account Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="font-medium text-slate-900">Sign out</p>
                  <p className="text-sm text-slate-500">Sign out of your account</p>
                </div>
                <Button variant="outline" onClick={handleLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
